import React, { useState } from 'react';
import { useStore, uid } from '../state/store';
import {
  Card, Chip, Banner, Empty, Text, Area, Select, Segmented, SectionHead, EvidenceBadge, Stat, Modal,
} from '../components/ui';
import {
  SAUDI_SIGNALS, SIGNAL_LABELS, SAUDI_PIPELINE_FIELDS, EVIDENCE_LEVELS, EMPTY_COPY,
} from '../lib/constants';

const OFFER_A_CUSTOMERS = ['Real Estate Contribution developer', 'Contribution manager', 'Arranger', 'Investment institution'];
const OFFER_B_CUSTOMERS = ['CMA institution', 'FinTech Experimental Permit firm', 'Fund / investment firm', 'Private-market operator'];

export default function Saudi() {
  const { state, d, set, add, patch, remove, today } = useStore();
  const saudi = d.saudi;
  const s = state.saudi;
  const [oppDraft, setOppDraft] = useState(null);

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>SAUDI — SCALE VALIDATION</h2>
          <Chip tone={saudi.count >= 3 ? 'green' : saudi.count === 2 ? 'amber' : ''}>{saudi.count} / 4 signals</Chip>
          <Chip>20% founder allocation</Chip>
        </div>
      </div>

      <Banner tone="warn">
        <strong>Saudi is not yet an active market.</strong> It must earn resources through evidence. Nothing here
        promotes Saudi to ACTIVE automatically — that requires a Strategy Exception or the Day 45 decision.
      </Banner>

      {/* ------------------------------------------------------- signals --- */}
      <section className="section">
        <SectionHead title="Saudi signals" hint="Each signal requires supporting evidence. A signal without a named counterparty is an opinion." />
        <div className="grid g4">
          {saudi.signals.map((sig) => (
            <div className={`signal ${sig.value}`} key={sig.key}>
              <div className="nm">{sig.label}</div>
              <div className="q">{sig.question}</div>
              <Segmented
                value={sig.value}
                onChange={(v) => set(`saudi.signals.${sig.key}`, v)}
                options={[
                  { value: 'none', label: 'None' },
                  { value: 'weak', label: 'Weak' },
                  { value: 'strong', label: 'Strong' },
                ]}
                toneMap={{ weak: 'amber', strong: 'green' }}
              />
              <div style={{ marginTop: 10 }}>
                <Area
                  label="Supporting evidence"
                  value={sig.note}
                  onChange={(v) => set(`saudi.signalNotes.${sig.key}`, v)}
                  rows={2}
                  placeholder="Who, when, what they did."
                />
              </div>
              {sig.unsupported && <Banner tone="stop">Unsupported. Record the counterparty or set this back to None.</Banner>}
            </div>
          ))}
        </div>

        <div className="grid g3" style={{ marginTop: 14 }}>
          <Card title="Signal count">
            <Stat label="Strong signals" value={<span className="num">{saudi.count} / 4</span>} size="lg" foot={`${saudi.weak} additional weak signal(s), which do not count`} />
          </Card>
          <Card title="Day 45 rule">
            <div className="tiny muted">
              <div>0–1 signal → <strong>Park</strong></div>
              <div>2 signals → <strong>Continue selectively</strong></div>
              <div>3–4 signals → <strong>Concentrate</strong></div>
            </div>
          </Card>
          <Card title="Current reading" right={<Chip tone={saudi.verdict === 'Concentrate' ? 'green' : saudi.verdict === 'Continue selectively' ? 'amber' : 'red'}>{saudi.verdict}</Chip>}>
            <div className="tiny muted">
              On today's evidence, Saudi would be <strong>{saudi.verdict.toLowerCase()}</strong> at Day 45. This is
              recalculated automatically at Day 30 and Day 45 on the gates page.
            </div>
          </Card>
        </div>
      </section>

      {/* -------------------------------------------------------- offers --- */}
      <section className="section">
        <SectionHead title="Offer A — Contribution OS" hint={OFFER_A_CUSTOMERS.join(' · ')} />
        <OfferPipeline offerKey="A" pipeline={s.offers.A.pipeline} notes={s.offers.A.notes} />
      </section>

      <section className="section">
        <SectionHead title="Offer B — Blocks Infrastructure" hint={OFFER_B_CUSTOMERS.join(' · ')} />
        <OfferPipeline offerKey="B" pipeline={s.offers.B.pipeline} notes={s.offers.B.notes} />
      </section>

      <section className="section">
        <SectionHead title="Offer C — REGA / Tokenization Path" hint="Strategic evidence, not direct revenue." />
        <Card>
          <div className="grid g2">
            <Text label="REGA contact" value={s.offerC.regaContact} onChange={(v) => set('saudi.offerC.regaContact', v)} />
            <Text label="PropTech Hub" value={s.offerC.proptechHub} onChange={(v) => set('saudi.offerC.proptechHub', v)} />
            <Text label="Sandbox status" value={s.offerC.sandboxStatus} onChange={(v) => set('saudi.offerC.sandboxStatus', v)} />
            <Text label="Next intake" value={s.offerC.nextIntake} onChange={(v) => set('saudi.offerC.nextIntake', v)} />
            <Text label="Working session" value={s.offerC.workingSession} onChange={(v) => set('saudi.offerC.workingSession', v)} />
            <Text label="Pilot criteria" value={s.offerC.pilotCriteria} onChange={(v) => set('saudi.offerC.pilotCriteria', v)} />
            <Text label="Technology evaluation" value={s.offerC.technologyEvaluation} onChange={(v) => set('saudi.offerC.technologyEvaluation', v)} />
            <Text label="Registry integration pathway" value={s.offerC.registryIntegration} onChange={(v) => set('saudi.offerC.registryIntegration', v)} />
          </div>
          <Banner>A government conversation counts as evidence only when it produces a concrete next gate.</Banner>
        </Card>
      </section>

      {/* -------------------------------------------------- opportunities -- */}
      <section className="section">
        <SectionHead
          title="Saudi opportunities"
          right={<button className="btn sm" onClick={() => setOppDraft(blankOpp(today))}>Add opportunity</button>}
        />
        {s.opportunities.length === 0 ? (
          <Empty title={EMPTY_COPY.saudi}>Name the first counterparties before asking for more resources.</Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Offer</th>
                    <th>Customer type</th>
                    <th>Stage</th>
                    <th>Evidence</th>
                    <th>Next action</th>
                    <th>Due</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {s.opportunities.map((o) => (
                    <tr key={o.id}>
                      <td>{o.account}</td>
                      <td>{o.offer}</td>
                      <td>{o.customerType}</td>
                      <td>{o.stage}</td>
                      <td><EvidenceBadge level={o.evidenceLevel || 'E0'} /></td>
                      <td>{o.nextAction || <span className="dim">none</span>}</td>
                      <td className="num">{o.nextActionDue || '—'}</td>
                      <td className="actions">
                        <button className="btn sm ghost" onClick={() => setOppDraft(o)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {oppDraft && (
        <OppModal
          draft={oppDraft}
          onClose={() => setOppDraft(null)}
          onSave={(o) => {
            const exists = s.opportunities.some((x) => x.id === o.id);
            if (exists) patch('saudi.opportunities', o.id, o);
            else add('saudi.opportunities', o);
          }}
          onDelete={(id) => remove('saudi.opportunities', id)}
        />
      )}
    </>
  );
}

function OfferPipeline({ offerKey, pipeline, notes }) {
  const { set } = useStore();
  return (
    <Card>
      <div className="grid g4">
        {SAUDI_PIPELINE_FIELDS.map((f) => (
          <div className="field" key={f.key} style={{ marginBottom: 6 }}>
            <label>{f.label}</label>
            <input
              type="number"
              className="num-input"
              value={pipeline[f.key] ?? 0}
              min="0"
              onChange={(e) => set(`saudi.offers.${offerKey}.pipeline.${f.key}`, Number(e.target.value) || 0)}
            />
          </div>
        ))}
      </div>
      <Area label="Notes" value={notes} onChange={(v) => set(`saudi.offers.${offerKey}.notes`, v)} rows={2} />
      {(pipeline.meetings > 0 && pipeline.painConfirmed === 0) && (
        <Banner tone="warn">
          {pipeline.meetings} meeting(s) and zero confirmed pain. Meetings without confirmed pain are E1, not validation.
        </Banner>
      )}
    </Card>
  );
}

function blankOpp(today) {
  return {
    id: uid('sa'),
    account: '',
    offer: 'A — Contribution OS',
    customerType: '',
    stage: 'Target',
    evidenceLevel: 'E0',
    nextAction: '',
    nextActionDue: '',
    lastTouch: today,
    isNew: true,
  };
}

function OppModal({ draft, onClose, onSave, onDelete }) {
  const [o, setO] = useState(draft);
  const set = (k) => (v) => setO((x) => ({ ...x, [k]: v }));
  return (
    <Modal
      title={draft.isNew ? 'Add Saudi opportunity' : draft.account || 'Opportunity'}
      onClose={onClose}
      footer={
        <>
          {!draft.isNew && <button className="btn danger" onClick={() => { onDelete(o.id); onClose(); }}>Delete</button>}
          <div className="spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => { onSave({ ...o, isNew: false }); onClose(); }}>Save</button>
        </>
      }
    >
      <div className="grid g2">
        <Text label="Account" value={o.account} onChange={set('account')} />
        <Select label="Offer" value={o.offer} onChange={set('offer')} options={['A — Contribution OS', 'B — Blocks Infrastructure', 'C — REGA / Tokenization']} />
        <Select label="Customer type" value={o.customerType} onChange={set('customerType')} options={['', ...OFFER_A_CUSTOMERS, ...OFFER_B_CUSTOMERS]} />
        <Select label="Stage" value={o.stage} onChange={set('stage')} options={['Target', 'Contacted', 'Discovery', 'Pain Confirmed', 'Technical Discovery', 'POC / Proposal', 'Budget', 'Paid', 'Lost / Parked']} />
        <Select label="Evidence level" value={o.evidenceLevel} onChange={set('evidenceLevel')} options={EVIDENCE_LEVELS.map((e) => ({ value: e.id, label: `${e.id} — ${e.name}` }))} />
        <Text label="Next action due" type="date" value={o.nextActionDue} onChange={set('nextActionDue')} />
      </div>
      <Text label="Next action" value={o.nextAction} onChange={set('nextAction')} />
    </Modal>
  );
}
